import React, { useState, useRef } from 'react';

const CgvUpload = ({ onUploadSuccess, credentials }) => {
    const [isDragging, setIsDragging] = useState(false);
    const [uploading, setUploading] = useState(false);
    const [message, setMessage] = useState(null);
    const fileInputRef = useRef(null);

    const validateFile = (file) => {
        if (!file.name.match(/^cgv_[a-zA-Z0-9\-_]+\.pdf$/)) {
            throw new Error('Le nom du fichier doit respecter le format: cgv_<nom>.pdf');
        }
        if (file.size > 10 * 1024 * 1024) {
            throw new Error('Le fichier est trop volumineux (max 10MB)');
        }
        if (file.type !== 'application/pdf') {
            throw new Error('Seuls les fichiers PDF sont acceptés');
        }
    };

    const uploadFile = async (file) => {
        try {
            validateFile(file);
            setUploading(true);
            setMessage(null);

            const formData = new FormData();
            formData.append('cgv', file);

            const response = await fetch(`${process.env.REACT_APP_SERVER_URI}/api/cgv/upload`, {
                method: 'POST',
                headers: {
                    'Authorization': 'Basic ' + window.btoa(`${credentials.username}:${credentials.password}`)
                },
                body: formData
            });

            const data = await response.json();

            if (!response.ok) {
                throw new Error(data.error || 'Erreur lors de l\'upload');
            }

            setMessage({ type: 'success', text: `Fichier ${file.name} uploadé avec succès !` });
            if (onUploadSuccess) onUploadSuccess();

        } catch (err) {
            setMessage({ type: 'error', text: err.message });
        } finally {
            setUploading(false);
            if (fileInputRef.current) {
                fileInputRef.current.value = '';
            }
        }
    };

    const handleDragEnter = (e) => {
        e.preventDefault();
        setIsDragging(true);
    };

    const handleDragLeave = (e) => {
        e.preventDefault();
        if (!e.currentTarget.contains(e.relatedTarget)) {
            setIsDragging(false);
        }
    };

    const handleDragOver = (e) => {
        e.preventDefault();
    };

    const handleDrop = (e) => {
        e.preventDefault();
        setIsDragging(false);
        
        const files = Array.from(e.dataTransfer.files);
        if (files.length > 0) {
            uploadFile(files[0]);
        }
    };

    const handleFileSelect = (e) => {
        const files = Array.from(e.target.files);
        if (files.length > 0) {
            uploadFile(files[0]);
        }
    };

    const handleClick = () => {
        if (fileInputRef.current) {
            fileInputRef.current.click();
        }
    };

    return (
        <div className="cgv-upload">
            <h3>Ajouter un fichier CGV</h3>
            
            <div 
                className={`upload-zone ${isDragging ? 'dragging' : ''} ${uploading ? 'uploading' : ''}`}
                onDragEnter={handleDragEnter}
                onDragLeave={handleDragLeave}
                onDragOver={handleDragOver}
                onDrop={handleDrop}
                onClick={handleClick}
            >
                <input
                    ref={fileInputRef}
                    type="file"
                    accept=".pdf"
                    onChange={handleFileSelect}
                    style={{ display: 'none' }}
                    disabled={uploading}
                />
                
                {uploading ? (
                    <div className="uploading-content">
                        <div className="spinner">⏳</div>
                        <p>Upload en cours...</p>
                    </div>
                ) : (
                    <div className="upload-content">
                        <div className="upload-icon">📁</div>
                        <p>
                            <strong>Glissez-déposez</strong> votre fichier CGV ici<br />
                            ou <strong>cliquez</strong> pour sélectionner
                        </p>
                        <small>Format requis: cgv_&lt;nom&gt;.pdf (max 10MB)</small>
                    </div>
                )}
            </div>

            {message && (
                <div className={`upload-message ${message.type}`}>
                    {message.text}
                </div>
            )}

            <div className="upload-rules">
                <h4>Règles de nommage:</h4>
                <ul>
                    <li>Le fichier doit commencer par "cgv_"</li>
                    <li>Suivi d'un nom (lettres, chiffres, tirets, underscores)</li>
                    <li>Extension .pdf obligatoire</li>
                    <li>Exemples: cgv_projet-a.pdf, cgv_produit_123.pdf</li>
                </ul>
            </div>
        </div>
    );
};

export default CgvUpload; 